import { httpClient } from './http-client';

/**
 * Template-driven CSV import.
 *
 * The fixed-format broker importer only accepts exports it can recognise by
 * content. This service backs the wizard for everything else: the user maps
 * columns and activity codes once, previews what would import, and can save
 * the mapping as a template for the next statement from the same broker.
 */

/** The canonical activity vocabulary the backend commits on. */
export const CANONICAL_ACTIVITIES = [
  'BUY',
  'SELL',
  'DIVIDEND',
  'INTEREST',
  'FEE',
  'DEPOSIT',
  'WITHDRAWAL',
  'TRANSFER_IN',
  'TRANSFER_OUT',
  'SPLIT',
  'ROC',
  'UNKNOWN',
] as const;
export type CanonicalActivity = (typeof CANONICAL_ACTIVITIES)[number];

/** The fields a CSV column can map onto. */
export const IMPORT_FIELDS = [
  { value: 'date', label: 'Date', required: true },
  { value: 'type', label: 'Activity type', required: false },
  { value: 'symbol', label: 'Symbol', required: false },
  { value: 'quantity', label: 'Quantity / shares', required: false },
  { value: 'price', label: 'Price', required: false },
  { value: 'fees', label: 'Fees / commission', required: false },
  { value: 'amount', label: 'Amount', required: false },
  { value: 'currency', label: 'Currency', required: false },
  { value: 'description', label: 'Description', required: false },
] as const;

export type RowStatus = 'valid' | 'warning' | 'needs_review' | 'error';

export interface GenericImportRow {
  row_no: number;
  raw: Record<string, string>;
  date: string;
  type: CanonicalActivity;
  broker_code: string;
  symbol: string;
  quantity: string;
  price: string;
  fees: string;
  amount: string;
  currency: string;
  description: string;
  status: RowStatus;
  message: string;
  external_id: string;
}

export interface BrokerCodeMapping {
  code: string;
  mapped: string;
  rows: number;
}

export interface GenericPreviewResponse {
  batch_id: string;
  headers: string[];
  suggested_column_map: Record<string, string>;
  column_map: Record<string, string>;
  type_map: Record<string, string>;
  date_format: string;
  decimal_style: string;
  rows: GenericImportRow[];
  broker_codes: BrokerCodeMapping[];
  summary: Record<string, number>;
}

export interface GenericCommitResult {
  created: number;
  skipped_duplicates: number;
  skipped_rows: number;
  errors: string[];
}

export interface ImportTemplate {
  id: string;
  household_id: string;
  name: string;
  broker_hint?: string;
  column_map: Record<string, string>;
  type_map: Record<string, string>;
  date_format?: string;
  decimal_style: string;
  created_at: string;
  updated_at: string;
}

export interface SaveTemplateRequest {
  name: string;
  broker_hint?: string;
  column_map: Record<string, string>;
  type_map: Record<string, string>;
  date_format?: string;
  decimal_style?: string;
}

export interface PreviewOptions {
  account_id: string;
  template_id?: string;
  column_map?: Record<string, string>;
  type_map?: Record<string, string>;
  date_format?: string;
  decimal_style?: string;
}

export class GenericImportService {
  /**
   * Upload the file with whatever mapping is in effect and get validated
   * rows back. Each call stores a fresh batch; commit targets the latest.
   */
  static async previewImport(file: File, opts: PreviewOptions): Promise<GenericPreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('account_id', opts.account_id);
    if (opts.template_id) formData.append('template_id', opts.template_id);
    // Maps travel as JSON strings inside the multipart form, because the
    // file has to travel alongside them in the same request.
    if (opts.column_map) formData.append('column_map', JSON.stringify(opts.column_map));
    if (opts.type_map) formData.append('type_map', JSON.stringify(opts.type_map));
    if (opts.date_format) formData.append('date_format', opts.date_format);
    if (opts.decimal_style) formData.append('decimal_style', opts.decimal_style);

    const raw = await httpClient.postForm<any>('/imports/preview', formData);
    return (raw as any).data || raw;
  }

  static async commitBatch(
    batchId: string,
    overrides: Array<{ row_no: number; type: CanonicalActivity }>,
  ): Promise<GenericCommitResult> {
    const raw = await httpClient.post<any>(`/imports/${batchId}/commit`, { overrides });
    return (raw as any).data || raw;
  }

  static async listTemplates(): Promise<ImportTemplate[]> {
    const raw = await httpClient.get<any>('/import-templates');
    return (raw as any).data || raw;
  }

  static async createTemplate(data: SaveTemplateRequest): Promise<ImportTemplate> {
    const raw = await httpClient.post<any>('/import-templates', data);
    return (raw as any).data || raw;
  }

  static async updateTemplate(id: string, data: SaveTemplateRequest): Promise<ImportTemplate> {
    const raw = await httpClient.put<any>(`/import-templates/${id}`, data);
    return (raw as any).data || raw;
  }

  static async deleteTemplate(id: string): Promise<void> {
    await httpClient.delete(`/import-templates/${id}`);
  }
}
